import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildRegistry,
  formatMetricFormula,
  metricDraftError,
  parseCoefficient,
  type MetricDef,
  type MetricRounding,
  type MetricUnit,
  type MetricUpsert,
} from '@lemontaigu/staff';
import { useMetricRegistry } from '@lemontaigu/staff/react';

// « Métriques » (ADR-0015) — the registry that gives the platform ONE definition
// of load. Before it, the same question (« how much ménage work is there
// today? ») had three answers living in three tables, two of which disagreed
// about how much a recouche costs.
//
// What is editable here follows from what each kind IS:
//
//   base          the eight board_days figures. LABEL only — the evaluator has
//                 to know how to read a stored field, so a base metric the
//                 operator invented would be a row nothing can resolve.
//   formula       fully editable. This is the operator's tool.
//   profileOutput published BY a staffing profile and maintained by trigger.
//                 Read-only here; its label tracks the profile's own name, so
//                 the place to rename it is Staffing.

const UNITS: { value: MetricUnit; label: string }[] = [
  { value: 'count', label: 'Nombre (sans unité)' },
  { value: 'equivalents', label: 'À blancs équivalents' },
  { value: 'personMinutes', label: 'Minutes de travail' },
  { value: 'heads', label: 'Personnes' },
];

const KIND_LABEL: Record<MetricDef['kind'], string> = {
  base: 'Saisie du jour',
  formula: 'Formule',
  profileOutput: 'Calculée par un profil Staffing',
};

const KIND_HINT: Record<MetricDef['kind'], string> = {
  base: 'Les chiffres que la réception saisit. Seul le libellé est modifiable.',
  formula: 'Une somme pondérée d’autres métriques. C’est ici que vous définissez vos propres charges.',
  profileOutput:
    'Produite par un profil Staffing (minutes de travail, à blancs équivalents, effectif). Pour la renommer, renommez le profil dans Staffing.',
};

interface MetricDraft {
  key: string;
  labelFr: string;
  unit: MetricUnit;
  rounding: MetricRounding;
  /** Coefficients as typed ('' = the metric doesn't count). */
  terms: Record<string, string>;
  active: boolean;
  inSummary: boolean;
  sort: string;
}

function draftOf(def: MetricDef): MetricDraft {
  return {
    key: def.key,
    labelFr: def.labelFr,
    unit: def.unit,
    rounding: def.rounding,
    terms: Object.fromEntries(Object.entries(def.formula ?? {}).map(([k, v]) => [k, String(v)])),
    active: def.active,
    inSummary: def.inSummary,
    sort: String(def.sort),
  };
}

function emptyDraft(nextSort: number): MetricDraft {
  return {
    key: '',
    labelFr: '',
    unit: 'count',
    // A new formula is a count of things by default, and counts round up — the
    // rule evaluateFormula has always applied (half a room is still a room).
    rounding: 'ceil',
    terms: {},
    active: true,
    // A new metric stays OUT of the summaries: a flag that defaulted on would
    // quietly crowd three surfaces the moment anyone added a metric.
    inSummary: false,
    sort: String(nextSort),
  };
}

function formulaOf(draft: MetricDraft): Record<string, number> {
  const formula: Record<string, number> = {};
  for (const [key, raw] of Object.entries(draft.terms)) {
    const coefficient = parseCoefficient(raw);
    if (coefficient !== null) formula[key] = coefficient;
  }
  return formula;
}

export function Metriques() {
  const registryPort = useMetricRegistry();
  const [metrics, setMetrics] = useState<MetricDef[] | null>(null);
  const [err, setErr] = useState('');
  const [editingKey, setEditingKey] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<MetricDraft | null>(null);
  const [draftErr, setDraftErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      setMetrics(await registryPort.load());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [registryPort]);

  useEffect(() => {
    void load();
  }, [load]);

  const all = metrics ?? [];
  const registry = useMemo(() => buildRegistry(all), [all]);
  const keys = useMemo(() => all.map((m) => m.key), [all]);

  const set = (patch: Partial<MetricDraft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const startEdit = (def: MetricDef | null) => {
    setDraftErr('');
    if (def) {
      setEditingKey(def.key);
      setDraft(draftOf(def));
    } else {
      setEditingKey('new');
      setDraft(emptyDraft(Math.max(100, ...all.map((m) => m.sort)) + 10));
    }
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft(null);
    setDraftErr('');
  };

  const save = async () => {
    if (!draft || editingKey === null) return;
    const existing = editingKey === 'new' ? null : registry.get(editingKey);
    // A base metric keeps its kind and its source field — only the label moves.
    const kind = existing?.kind ?? 'formula';
    const payload: MetricUpsert = {
      key: draft.key.trim(),
      labelFr: draft.labelFr.trim(),
      unit: draft.unit,
      kind,
      rounding: draft.rounding,
      sourceField: existing?.sourceField ?? null,
      formula: kind === 'formula' ? formulaOf(draft) : null,
      active: draft.active,
      inSummary: draft.inSummary,
      sort: draft.sort.trim() === '' ? 100 : Number(draft.sort.trim()),
    };
    const invalid = metricDraftError(payload, keys, editingKey === 'new');
    if (invalid) {
      setDraftErr(invalid);
      return;
    }
    setBusy(true);
    setDraftErr('');
    try {
      await registryPort.save(payload);
      cancelEdit();
      void load();
    } catch (e) {
      // The DB's validate trigger names exactly what is wrong (unknown
      // reference, cycle) — surfaced verbatim rather than reworded.
      setDraftErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (def: MetricDef) => {
    if (!window.confirm(`Supprimer la métrique « ${def.labelFr} » ?`)) return;
    setBusy(true);
    setErr('');
    try {
      await registryPort.remove(def.key);
      void load();
    } catch (e) {
      // The delete guard lists the formulas still depending on it — the one
      // thing the operator needs in order to act.
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const groups: MetricDef['kind'][] = ['formula', 'profileOutput', 'base'];

  return (
    <div className="planning">
      <div className="equipe-head">
        <h2 className="section-title">Métriques</h2>
        <span className="equipe-hint">
          Une seule définition de la charge, réutilisable partout. Une activité pointe vers une métrique pour sa
          charge et pour son badge ; un profil Staffing en publie (à blancs équivalents, minutes, effectif).
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {metrics === null && <p className="staff-empty">Chargement…</p>}

      {metrics !== null && editingKey === null && (
        <p className="planning-form-wide">
          <button type="button" className="previsionnel-apply" onClick={() => startEdit(null)}>
            + Nouvelle métrique
          </button>
        </p>
      )}

      {draft && editingKey !== null && (
        <div className="planning-card">
          <div className="planning-card-head">
            <strong>{editingKey === 'new' ? 'Nouvelle métrique' : draft.labelFr}</strong>
          </div>
          <div className="planning-form">
            <label>
              Libellé
              <input value={draft.labelFr} onChange={(e) => set({ labelFr: e.target.value })} />
            </label>
            <label>
              Clé
              <input
                value={draft.key}
                disabled={editingKey !== 'new'}
                onChange={(e) => set({ key: e.target.value })}
              />
              <span className="planning-hint">
                {editingKey === 'new'
                  ? 'camelCase, sans accent. Définitive : les formules y font référence.'
                  : 'La clé ne change pas — les formules y font référence.'}
              </span>
            </label>
            <label>
              Unité
              <select value={draft.unit} onChange={(e) => set({ unit: e.target.value as MetricUnit })}>
                {UNITS.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Arrondi
              <select
                value={draft.rounding}
                onChange={(e) => set({ rounding: e.target.value as MetricRounding })}
              >
                <option value="ceil">Arrondi au supérieur</option>
                <option value="none">Valeur exacte (décimales)</option>
              </select>
              <span className="planning-hint">
                Une demi-chambre reste une chambre à faire : arrondir au supérieur pour des comptages. Garder les
                décimales pour des équivalents (« 7,3 »).
              </span>
            </label>

            {(editingKey === 'new' || registry.get(editingKey)?.kind === 'formula') && (
              <div className="planning-form-wide planning-formula">
                <span className="planning-formula-title">Formule = somme pondérée</span>
                <span className="planning-hint">
                  Laisser vide une métrique qui ne compte pas. Une formule peut référencer une autre formule ou une
                  métrique publiée par un profil Staffing.
                </span>
                <div className="planning-formula-grid">
                  {all
                    .filter((m) => m.key !== draft.key)
                    .map((m) => (
                      <label key={m.key} className="planning-formula-cell">
                        {m.labelFr}
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="0"
                          value={draft.terms[m.key] ?? ''}
                          onChange={(e) => set({ terms: { ...draft.terms, [m.key]: e.target.value } })}
                        />
                      </label>
                    ))}
                </div>
                <span className="planning-formula-preview">{formatMetricFormula(registry, formulaOf(draft))}</span>
              </div>
            )}

            <label className="planning-check planning-form-wide">
              <input type="checkbox" checked={draft.active} onChange={(e) => set({ active: e.target.checked })} />
              Active
            </label>
            <label className="planning-check planning-form-wide">
              <input
                type="checkbox"
                checked={draft.inSummary}
                onChange={(e) => set({ inSummary: e.target.checked })}
              />
              Afficher en résumé — vue Mois, et en ligne « dérivée » dans les grilles Semaine et Jour
            </label>
            {draftErr && <p className="auth-err planning-form-wide">{draftErr}</p>}
            <p className="planning-form-wide">
              <button type="button" className="previsionnel-apply" disabled={busy} onClick={() => void save()}>
                {busy ? '…' : 'Enregistrer'}
              </button>{' '}
              <button type="button" onClick={cancelEdit}>
                Annuler
              </button>
            </p>
          </div>
        </div>
      )}

      {groups.map((kind) => {
        const rows = all.filter((m) => m.kind === kind);
        if (rows.length === 0) return null;
        return (
          <section key={kind} className="planning-card">
            <header className="planning-card-head">
              <strong>{KIND_LABEL[kind]}</strong>
              <span className="equipe-hint">{KIND_HINT[kind]}</span>
            </header>
            <ul className="metric-list">
              {rows.map((m) => (
                <li key={m.key} className={`metric-row${m.active ? '' : ' is-inactive'}`}>
                  <span className="metric-label">{m.labelFr}</span>
                  <code className="metric-key">{m.key}</code>
                  {m.kind === 'formula' && m.formula && (
                    <span className="metric-formula">{formatMetricFormula(registry, m.formula)}</span>
                  )}
                  {m.kind === 'base' && m.sourceField && <span className="metric-formula">saisie du jour</span>}
                  {m.inSummary && (
                    <span className="metric-summary" title="Affichée dans les résumés Mois / Semaine / Jour">
                      résumé
                    </span>
                  )}
                  <span className="metric-unit">{UNITS.find((u) => u.value === m.unit)?.label}</span>
                  {kind !== 'profileOutput' && (
                    <button type="button" disabled={busy} onClick={() => startEdit(m)}>
                      Modifier
                    </button>
                  )}
                  {kind === 'formula' && (
                    <button type="button" disabled={busy} onClick={() => void remove(m)}>
                      Supprimer
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
